class CartVariantSelector extends HTMLElement {
  constructor() {
    super();
    this.variants = [];
  }

  connectedCallback() {
    this.selects = this.querySelectorAll('[data-option-index]');
    this.variantsJson = this.querySelector('[data-variants-json]');
    this.lineKey = this.dataset.lineKey;
    this.currentVariantId = parseInt(this.dataset.variantId, 10);
    this.productId = this.dataset.productId;
    this.parseVariants();
    this.attachEvents();
    this.updateSelectStates();
  }

  parseVariants() {
    if (this.variantsJson) {
      try {
        this.variants = JSON.parse(this.variantsJson.textContent);
      } catch (e) {
        console.error('Error parsing variants JSON:', e);
        this.variants = [];
      }
    }
  }

  attachEvents() {
    this.selects.forEach((select) => {
      select.addEventListener('change', this.handleOptionChange.bind(this));
    });
  }

  handleOptionChange() {
    const selectedOptions = this.getSelectedOptions();
    const newVariant = this.findVariant(selectedOptions);

    if (newVariant && newVariant.id !== this.currentVariantId) {
      if (newVariant.available) {
        this.changeVariant(newVariant);
      } else {
        this.revertToCurrentVariant();
        this.showUnavailableMessage();
      }
    }

    this.updateSelectStates();
  }

  getSelectedOptions() {
    return Array.from(this.selects).map((select) => select.value);
  }

  findVariant(options) {
    return this.variants.find((variant) => {
      return variant.options.every((option, index) => option === options[index]);
    });
  }

  getCurrentVariant() {
    return this.variants.find((variant) => variant.id === this.currentVariantId);
  }

  revertToCurrentVariant() {
    const currentVariant = this.getCurrentVariant();
    if (currentVariant) {
      this.selects.forEach((select, index) => {
        select.value = currentVariant.options[index];
      });
    }
  }

  updateSelectStates() {
    const selectedOptions = this.getSelectedOptions();

    this.selects.forEach((select, selectIndex) => {
      const options = select.querySelectorAll('option');

      options.forEach((option) => {
        const testOptions = [...selectedOptions];
        testOptions[selectIndex] = option.value;
        const variant = this.findVariant(testOptions);

        if (variant && !variant.available) {
          option.disabled = true;
          if (!option.textContent.includes('—')) {
            option.textContent = `${option.value} — ${this.getSoldOutLabel()}`;
          }
        } else {
          option.disabled = false;
          option.textContent = option.value;
        }
      });
    });
  }

  getSoldOutLabel() {
    return window.theme?.translations?.soldOut || 'Sold out';
  }

  async changeVariant(newVariant) {
    const lineItem = this.closest('[data-line-item-key]');
    const cartItems = this.closest('cart-items');

    if (!lineItem || !cartItems) return;

    this.setLoading(true);

    try {
      const quantity = this.getCurrentQuantity(lineItem);
      const cart = await this.updateCartVariant(newVariant.id, quantity);

      this.currentVariantId = newVariant.id;
      this.dataset.variantId = newVariant.id;

      this.updateLineItemUI(lineItem, newVariant);
      this.updateCartUI(cart);
    } catch (error) {
      console.error('Error changing variant:', error);
      this.revertToCurrentVariant();
      this.showErrorMessage(error);
    } finally {
      this.setLoading(false);
    }
  }

  getCurrentQuantity(lineItem) {
    const input = lineItem.querySelector('[data-quantity-input]');
    return input ? parseInt(input.value, 10) : 1;
  }

  async updateCartVariant(newVariantId, quantity) {
    const sections = this.getSectionsToRender().join(',');
    const lineKey = this.lineKey || this.dataset.lineKey;

    const response = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        id: lineKey,
        quantity: 0,
        sections,
        sections_url: window.location.pathname,
      }),
    });

    if (!response.ok) {
      throw new Error(window.theme?.translations?.genericError || 'Failed to remove current item');
    }

    const removeData = await response.json();

    const addResponse = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        id: newVariantId,
        quantity: quantity,
        sections,
        sections_url: window.location.pathname,
      }),
    });

    if (!addResponse.ok) {
      const errorData = await addResponse.json();
      throw new Error(errorData.description || window.theme?.translations?.genericError || 'Failed to add new variant');
    }

    const cart = await addResponse.json();

    // cart/add.js with a single id returns the line item object directly (not { items: [...] })
    this.lineKey = cart.key || this.lineKey;
    this.dataset.lineKey = this.lineKey;

    return cart;
  }

  getSectionsToRender() {
    const cartDrawer = document.querySelector('cart-drawer');
    const cartPage = document.querySelector('cart-page');
    const sections = [];

    if (cartDrawer) {
      sections.push('cart-drawer');
    }
    if (cartPage) {
      sections.push('cart');
    }

    return sections.length > 0 ? sections : ['cart-drawer', 'cart'];
  }

  updateLineItemUI(lineItem, newVariant) {
    const priceElement = lineItem.querySelector('.cart-line-item__price-value');
    if (priceElement && newVariant.price) {
      priceElement.textContent = this.formatMoney(newVariant.price);
    }

    if (newVariant.featured_image) {
      const imageElement = lineItem.querySelector('.cart-line-item__thumbnail img');
      if (imageElement) {
        imageElement.src = newVariant.featured_image.src;
        imageElement.alt = newVariant.featured_image.alt;
      }
    }
  }

  formatMoney(cents) {
    const amount = cents / 100;
    const currency = window.Shopify?.currency?.active || 'EUR';
    const locale = window.Shopify?.locale || 'en';

    const formatter = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });

    let formatted = formatter.format(amount);
    formatted = formatted.replace(/([,.])(0{1,2})(?=\s*[^\d]|$)/, '');

    return formatted;
  }

  updateCartUI(cart) {
    theme.pubsub.publish(theme.pubsub.events.CART_UPDATE, {
      cart,
      source: 'cart-variant-selector',
    });
  }

  setLoading(isLoading) {
    const lineItem = this.closest('[data-line-item-key]');
    if (lineItem) {
      lineItem.classList.toggle('cart-line-item--loading', isLoading);
      const buttons = lineItem.querySelectorAll('button, select');
      buttons.forEach((el) => {
        el.disabled = isLoading;
      });
    }
  }

  showUnavailableMessage() {
    const message = window.theme?.translations?.variantUnavailable || 'This variant is not available';
    this.showToast(message, 'warning');
  }

  showErrorMessage(error) {
    const message = error.message || window.theme?.translations?.genericError || 'An error occurred';
    this.showToast(message, 'error');
    theme.pubsub.publish(theme.pubsub.events.CART_ERROR, { error });
  }

  showToast(message, type = 'info') {
    console.warn(`[${type}] ${message}`);
  }
}

customElements.define('cart-variant-selector', CartVariantSelector);
